import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from '@/hooks/use-toast';
import { ALL_ACHIEVEMENTS, type SurfLevel } from '@/lib/surf-progression';
import type { Database } from '@/integrations/supabase/types';
import type { SkillLevel } from '@/lib/planner-vocabulary';

type SurfLevelEnum = Database['public']['Enums']['surf_level_enum'];

const LEVEL_LABEL_TO_ENUM: Record<SurfLevel, SurfLevelEnum> = {
  'Absolute Beginner': 'absolute_beginner',
  Beginner: 'beginner',
  Intermediate: 'intermediate',
  Advanced: 'advanced',
  Expert: 'expert',
};

/** Exposed for Auth.tsx's best-effort signup upsert, which writes surf_level
 * alongside full_name using the same raw supabase.from() call rather than
 * this hook's own mutation (there's no session-backed query cache to update
 * yet at that point in the flow). */
export const surfLevelToDbEnum = (level: SurfLevel): SurfLevelEnum => LEVEL_LABEL_TO_ENUM[level];

const LEVEL_ENUM_TO_LABEL: Record<SurfLevelEnum, SurfLevel> = {
  absolute_beginner: 'Absolute Beginner',
  beginner: 'Beginner',
  intermediate: 'Intermediate',
  advanced: 'Advanced',
  expert: 'Expert',
};

// Write-side and read-side defensive validation both check against this --
// src/lib/surf-progression.ts's ALL_ACHIEVEMENTS is the single canonical
// taxonomy, deliberately not mirrored into a DB constraint (see the migration
// comment on user_achievements). Never trust an achievement_id from Supabase,
// in either direction, without checking it here first.
const KNOWN_ACHIEVEMENT_IDS = new Set(ALL_ACHIEVEMENTS.map((a) => a.id));

/**
 * In-memory (not persisted) handoff for the sign-up flow. `supabase.auth.signUp()`
 * fires `onAuthStateChange` internally *before* its own promise resolves, and
 * Auth.tsx's auto-redirect effect reacts to that immediately -- by the time this
 * module's own component has unmounted, its local `suLevel` state is gone. This
 * lets `handleSignUp` in Auth.tsx still find the chosen level afterwards, the same
 * way it already does for `full_name` via signUp's own metadata. Cleared after one
 * successful read so it can never leak into a later, unrelated sign-up in the same
 * tab. See Auth.tsx for where this is set and consumed.
 */
let pendingSignupLevel: SurfLevel | null = null;

export const setPendingSignupLevel = (level: SurfLevel) => {
  pendingSignupLevel = level;
};

export const consumePendingSignupLevel = (): SurfLevel | null => {
  const level = pendingSignupLevel;
  pendingSignupLevel = null;
  return level;
};

// One-directional map from My Swell Breaks' 5-level self-assessment down to
// the trip planner's locked 4-value SkillLevel contract (planner-vocabulary.ts)
// -- exactly the "deliberate vocabulary-mapping decision" surf-progression.ts's
// own header comment anticipated, not a merge of the two taxonomies. Lossy in
// one direction only: 'Absolute Beginner' collapses into 'beginner', the same
// floor the planner has always offered (it has never had a level below
// 'beginner' for anyone to pick), so this loses no option a planner user could
// previously reach themselves. Does not change SKILL_LEVEL_VALUES, the RPC
// contract, or scoring -- it only supplies a smarter default for what the
// planner sends, never a new accepted value.
const SURF_LEVEL_TO_PLANNER_SKILL_LEVEL: Record<SurfLevel, SkillLevel> = {
  'Absolute Beginner': 'beginner',
  Beginner: 'beginner',
  Intermediate: 'intermediate',
  Advanced: 'advanced',
  Expert: 'expert',
};

export const surfLevelToPlannerSkillLevel = (level: SurfLevel): SkillLevel =>
  SURF_LEVEL_TO_PLANNER_SKILL_LEVEL[level];

export const useSurfLevel = () => {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['surf-level', user?.id],
    enabled: !!user,
    queryFn: async (): Promise<SurfLevel | null> => {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('surf_level')
        .eq('id', user!.id)
        .maybeSingle();
      if (error) throw error;
      return data?.surf_level ? LEVEL_ENUM_TO_LABEL[data.surf_level] : null;
    },
  });
};

export const useSetSurfLevel = () => {
  const { user } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (level: SurfLevel) => {
      if (!user) throw new Error('You need to sign in first.');
      const enumValue = LEVEL_LABEL_TO_ENUM[level];

      // Try update first (the common case -- a profile row already exists).
      // Not a plain update-and-hope: a user_profiles row isn't guaranteed to
      // exist yet (e.g. the email-confirmation signup gap -- see Auth.tsx --
      // means a freshly-confirmed user can reach this onboarding step with no
      // row at all), and update() against a missing row silently affects zero
      // rows with no error, which would show the choice as "saved" while
      // persisting nothing. .select().maybeSingle() makes that case visible
      // instead of silent. Deliberately NOT using .upsert(): confirmed
      // empirically that PostgREST's upsert-via-POST requires full
      // table-level UPDATE privilege even with column-level grants in place,
      // which would mean widening user_profiles' UPDATE grant to the whole
      // row -- undoing the F1 role-escalation column-grant model entirely.
      const { data: updated, error: updateError } = await supabase
        .from('user_profiles')
        .update({ surf_level: enumValue })
        .eq('id', user.id)
        .select('id')
        .maybeSingle();
      if (updateError) throw updateError;
      if (updated) return;

      const { error: insertError } = await supabase
        .from('user_profiles')
        .insert({ id: user.id, surf_level: enumValue });
      if (insertError) throw insertError;
    },
    onSuccess: (_data, level) => {
      qc.setQueryData(['surf-level', user?.id], level);
    },
    onError: (err: Error) => {
      toast({ title: 'Something went wrong', description: err.message, variant: 'destructive' });
    },
  });
};

export const useAchievements = () => {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['achievements', user?.id],
    enabled: !!user,
    queryFn: async (): Promise<Set<string>> => {
      const { data, error } = await supabase
        .from('user_achievements')
        .select('achievement_id')
        .eq('user_id', user!.id);
      if (error) throw error;

      // Read-side defensive validation: never trust achievement_id values
      // returned by Supabase for progress/count/UI/recommendation purposes --
      // filter to the canonical taxonomy before using them for anything.
      // Unknown ids are silently ignored, never deleted (not this client's
      // call to destroy someone's data over an id mismatch it can't explain).
      const rows = data ?? [];
      const known = rows.filter((row) => KNOWN_ACHIEVEMENT_IDS.has(row.achievement_id));
      if (import.meta.env.DEV && known.length !== rows.length) {
        const unknown = rows.filter((row) => !KNOWN_ACHIEVEMENT_IDS.has(row.achievement_id));
        console.warn(
          '[useAchievements] ignoring achievement_id(s) not in the canonical taxonomy:',
          unknown.map((row) => row.achievement_id)
        );
      }
      return new Set(known.map((row) => row.achievement_id));
    },
  });
};

export const useToggleAchievement = () => {
  const { user } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ achievementId, completed }: { achievementId: string; completed: boolean }) => {
      if (!user) throw new Error('You need to sign in first.');
      // Write-side defensive validation: never insert an id outside the
      // canonical taxonomy -- this table has no DB-level constraint mirroring
      // it on purpose (see the migration comment), so the application layer
      // is the only gate.
      if (!KNOWN_ACHIEVEMENT_IDS.has(achievementId)) {
        throw new Error(`Unknown achievement id: ${achievementId}`);
      }
      if (completed) {
        const { error } = await supabase
          .from('user_achievements')
          .insert({ user_id: user.id, achievement_id: achievementId });
        // A duplicate insert (already completed -- e.g. a double-click) is a
        // no-op, not a failure.
        if (error && error.code !== '23505') throw error;
      } else {
        const { error } = await supabase
          .from('user_achievements')
          .delete()
          .eq('user_id', user.id)
          .eq('achievement_id', achievementId);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['achievements', user?.id] });
    },
    onError: (err: Error) => {
      toast({ title: 'Something went wrong', description: err.message, variant: 'destructive' });
    },
  });
};
